import {NodeSelection, Plugin, PluginKey, TextSelection} from "prosemirror-state";
import {normalizeWikidataEntityDomEl} from "../../entityMentions/entityHoverSuppression";
import {resolveEntityAvatarUrl} from "../../../utils/entityAvatarUrl";
import {isLinkedEntityRef} from "../../../utils/entityLinks";

export const inlineInteractionPluginKey = new PluginKey("inlineInteraction");

function getAnchorPositionFromPos(view, pos) {
    if (!view || typeof pos !== "number") return null;

    try {
        const coords = view.coordsAtPos(pos);
        return {
            left: Math.round((coords.left + coords.right) / 2),
            top: Math.round(coords.top),
        };
    } catch (error) {
        return null;
    }
}

function getNodeAnchorEl(view, pos) {
    const dom = view?.nodeDOM?.(pos);
    return dom instanceof HTMLElement ? dom : null;
}

function isLinkedWikidataMark(mark) {
    return isLinkedEntityRef(mark?.attrs?.id);
}

function pickPreferredWikidataMark(marks) {
    const wikidataMarks = marks.filter((candidate) => candidate.type.name === 'wikidataEntity');
    if (!wikidataMarks.length) return null;
    return wikidataMarks.find(isLinkedWikidataMark) || wikidataMarks[0];
}

function getWikidataMarkAtPos(doc, pos) {
    if (!doc || typeof pos !== "number") return null;
    if (pos < 0 || pos > doc.content.size) return null;

    const mark = pickPreferredWikidataMark(doc.resolve(pos).marks());
    if (mark) return {mark, position: pos};

    if (pos > 0) {
        const before = pickPreferredWikidataMark(doc.resolve(pos - 1).marks());
        if (before) return {mark: before, position: pos - 1};
    }

    if (pos < doc.content.size) {
        const after = pickPreferredWikidataMark(doc.resolve(pos + 1).marks());
        if (after) return {mark: after, position: pos + 1};
    }

    return null;
}

/**
 * First body caret position that does not resolve to a wikidata inline target.
 * Used on editor init so the default selection does not auto-open entity popovers.
 */
export function findNeutralCaretPos(doc) {
    if (!doc) return 1;

    let minPos = 1;
    if (doc.firstChild?.type.name === "document_header") {
        minPos = doc.firstChild.nodeSize;
        if (doc.child(1)?.type.name === "header_separator") {
            minPos += doc.child(1).nodeSize;
        }
    }

    for (let pos = Math.max(minPos, doc.content.size - 1); pos >= minPos; pos -= 1) {
        if (!getWikidataMarkAtPos(doc, pos)) {
            return pos;
        }
    }

    return minPos;
}

function getWikidataAnchorEl(view, pos) {
    if (!view?.dom || typeof pos !== "number") return null;

    const viewDom = view.dom;
    const posCandidates = [pos, Math.max(0, pos - 1), pos + 1];

    for (const candidatePos of posCandidates) {
        try {
            const domAtPos = view.domAtPos(candidatePos);
            const container = domAtPos?.node;
            const element = container instanceof Element
                ? container
                : container?.parentElement;

            if (!element) continue;
            const entityEl = element.closest(".wikidata-entity");
            if (entityEl && viewDom.contains(entityEl)) {
                return normalizeWikidataEntityDomEl(entityEl);
            }
        } catch (error) {
            // Ignore invalid candidate positions and continue fallback checks.
        }
    }

    return null;
}

export function resolveInlineNodeTarget(state) {
    const {selection} = state;

    // Only trigger for explicit NodeSelection — not for caret adjacent to the node.
    // ProseMirror creates NodeSelection when clicking or arrow-navigating into
    // selectable inline atoms, which is the correct trigger for the info panel.
    if (selection instanceof NodeSelection) {
        if (selection.node.type.name === "userMention" || selection.node.type.name === "hashtag") {
            return {
                type: selection.node.type.name === "userMention" ? "mention" : "hashtag",
                node: selection.node,
                nodePos: selection.from,
            };
        }
    }

    return null;
}

export function resolveTargetFromSelection(state) {
    const inlineNodeTarget = resolveInlineNodeTarget(state);
    if (inlineNodeTarget) return inlineNodeTarget;

    const {selection, doc} = state;
    if (!(selection instanceof TextSelection) || !selection.empty) return null;

    const markResult = getWikidataMarkAtPos(doc, selection.from);
    if (!markResult) return null;

    return {
        type: "wikidata",
        mark: markResult.mark,
        position: markResult.position,
    };
}

export function buildMentionPayload(target, trigger, view) {
    const attrs = target.node.attrs || {};
    const anchorEl = getNodeAnchorEl(view, target.nodePos);

    return {
        type: "mention",
        trigger,
        id: attrs.id,
        uid: attrs.id,
        label: attrs.label,
        name: attrs.name,
        email: attrs.email,
        position: attrs.position,
        headshot: attrs.headshot,
        anchorEl,
        anchorPosition: getAnchorPositionFromPos(view, target.nodePos),
        nodePos: target.nodePos,
        view,
    };
}

export function buildHashtagPayload(target, trigger, view) {
    const attrs = target.node.attrs || {};
    const anchorEl = getNodeAnchorEl(view, target.nodePos);

    return {
        type: "hashtag",
        trigger,
        id: attrs.id,
        label: attrs.label,
        docId: attrs.docId,
        anchorEl,
        anchorPosition: getAnchorPositionFromPos(view, target.nodePos),
        nodePos: target.nodePos,
        view,
    };
}

export function buildWikidataPayload(target, trigger, view) {
    const attrs = target.mark.attrs || {};
    const anchorEl = getWikidataAnchorEl(view, target.position);
    return {
        type: "wikidata",
        trigger,
        id: attrs.id,
        uid: attrs.uid || null,
        label: attrs.label,
        url: attrs.url,
        description: attrs.description || null,
        entityType: attrs.entityType || null,
        confidence: attrs.confidence ?? null,
        nerScore: attrs.nerScore ?? attrs.confidence ?? null,
        linkingScore: attrs.linkingScore ?? null,
        imageUrl: resolveEntityAvatarUrl({
            wikidataId: attrs.id,
            imageUrl: attrs.imageUrl || null,
        }),
        anchorEl,
        anchorPosition: getAnchorPositionFromPos(view, target.position),
        position: target.position,
        view,
    };
}

export function buildInteractionPayload(target, trigger, view) {
    if (!target) return null;

    if (target.type === "mention") {
        return buildMentionPayload(target, trigger, view);
    }

    if (target.type === "hashtag") {
        return buildHashtagPayload(target, trigger, view);
    }

    if (target.type === "wikidata") {
        return buildWikidataPayload(target, trigger, view);
    }

    return null;
}

export function getPayloadSignature(payload) {
    if (!payload) return "none";

    if (payload.type === "mention" || payload.type === "hashtag") {
        return `${payload.type}:${payload.nodePos}:${payload.id || ""}`;
    }

    if (payload.type === "wikidata") {
        return `${payload.type}:${payload.position}:${payload.uid || payload.id || ""}`;
    }

    return payload.type;
}

function notifyInteraction(payload, callbacks = {}) {
    const {
        onInlineInteractionChange,
        onEntityClick,
        onUserMentionClick,
        onHashtagClick,
        onInlineInteractionClear,
    } = callbacks;

    onInlineInteractionChange?.(payload || null);

    if (!payload) {
        onInlineInteractionClear?.();
        return;
    }

    if (payload.type === "wikidata") {
        onEntityClick?.(payload);
        return;
    }

    if (payload.type === "mention") {
        onUserMentionClick?.(payload);
        return;
    }

    if (payload.type === "hashtag") {
        onHashtagClick?.(payload);
    }
}

/**
 * Find the contiguous document range (from, to) covered by the wikidataEntity mark
 * instance at `position`. When `uid` is provided, only segments carrying a mark with
 * the same uid are joined; otherwise any wikidataEntity mark at the anchor is used.
 * Mirrors the range-walk used by WikidataEntityCard's Unlink so manual re-linking
 * targets the full mention span. Returns null when no mark is found.
 *
 * @param {Object} state - ProseMirror editor state
 * @param {number} position - Document position inside (or adjacent to) the mark
 * @param {string|null} uid - Optional mark instance uid to disambiguate
 * @returns {{from: number, to: number, mark: Object}|null}
 */
export function findWikidataMarkRange(state, position, uid = null) {
    const markType = state?.schema?.marks?.wikidataEntity;
    if (!markType || !state?.doc || typeof position !== "number") return null;

    const docSize = state.doc.content.size;
    const safePos = Math.max(0, Math.min(position, docSize));

    const findMarkAtPos = (pos) => {
        if (pos < 0 || pos > docSize) return null;
        const marksHere = state.doc.resolve(pos).marks();
        if (uid) {
            return marksHere.find((m) => m.type === markType && m.attrs.uid === uid) || null;
        }
        return marksHere.find((m) => m.type === markType) || null;
    };

    let mark = findMarkAtPos(safePos);
    let anchorPos = safePos;
    if (!mark && safePos > 0) {
        mark = findMarkAtPos(safePos - 1);
        anchorPos = safePos - 1;
    }
    if (!mark) return null;

    const markUid = uid || mark.attrs.uid || null;
    const isTargetMark = (candidate) => {
        if (candidate.type !== markType) return false;
        if (markUid) return candidate.attrs.uid === markUid;
        return candidate.eq(mark);
    };

    const segments = [];
    state.doc.descendants((node, pos) => {
        if (!node.isText) return;
        segments.push({
            from: pos,
            to: pos + node.nodeSize,
            hasMark: node.marks.some(isTargetMark),
        });
    });

    let segmentIndex = segments.findIndex((seg) => seg.hasMark && anchorPos >= seg.from && anchorPos <= seg.to);
    if (segmentIndex === -1 && anchorPos > 0) {
        const fallbackPos = anchorPos - 1;
        segmentIndex = segments.findIndex((seg) => seg.hasMark && fallbackPos >= seg.from && fallbackPos <= seg.to);
    }
    if (segmentIndex === -1) return null;

    let from = segments[segmentIndex].from;
    let to = segments[segmentIndex].to;
    for (let i = segmentIndex - 1; i >= 0; i -= 1) {
        const seg = segments[i];
        if (!seg.hasMark || seg.to !== from) break;
        from = seg.from;
    }
    for (let i = segmentIndex + 1; i < segments.length; i += 1) {
        const seg = segments[i];
        if (!seg.hasMark || seg.from !== to) break;
        to = seg.to;
    }

    return {from, to, mark};
}

export function createInlineInteractionPlugin(callbacks = {}) {
    return new Plugin({
        key: inlineInteractionPluginKey,
        view(view) {
            let lastSignature = "none";

            const emitSelectionInteraction = (editorView) => {
                const target = resolveTargetFromSelection(editorView.state);
                const payload = buildInteractionPayload(target, "caret", editorView);
                const signature = getPayloadSignature(payload);
                if (signature === lastSignature) return;

                lastSignature = signature;
                notifyInteraction(payload, callbacks);
            };

            emitSelectionInteraction(view);

            return {
                update(editorView, prevState) {
                    const selectionChanged = !prevState.selection.eq(editorView.state.selection);
                    const docChanged = prevState.doc !== editorView.state.doc;
                    if (!selectionChanged && !docChanged) return;

                    emitSelectionInteraction(editorView);
                },
            };
        },
    });
}
